import React from 'react';
import classnames from 'classnames';

import ResearchItemBody from '@buyside/components/presentation/ResearchItem/ResearchItemBody/researchItemBody';
import ResearchItemFooter from '@buyside/components/presentation/ResearchItem/ResearchItemFooter/researchItemFooter';
import ResearchItemHeader from '@buyside/components/presentation/ResearchItem/ResearchItemHeader/researchItemHeader';

import {
  IdeaResearchItemFragment,
  Maybe,
  WhitePaperResearchItemFragment
} from '@interfaces/graphql-typings';

import styles from './researchItemStyles.scss';

export enum ResearchTypes {
  IDEA = 'Idea',
  WHITE_PAPER = 'WhitePaper'
}

export type SharedResearchItemProps = {
  showUserDetails: boolean;
  narrow?: boolean;
  postedAt?: Maybe<string>;
};

export type IdeaResearchItemProps = IdeaResearchItemFragment &
  SharedResearchItemProps & {
    researchType: ResearchTypes.IDEA;
    isPublic?: boolean;
  };

export type WhitePaperResearchItemProps = WhitePaperResearchItemFragment &
  SharedResearchItemProps & {
    researchType: ResearchTypes.WHITE_PAPER;
  };

type ResearchItemProps = IdeaResearchItemProps | WhitePaperResearchItemProps;

const ResearchItem: React.FC<ResearchItemProps> = props => {
  const { id, researchType, showUserDetails, narrow, tagList } = props;

  if (researchType === ResearchTypes.WHITE_PAPER)
    return (
      <div className={styles.researchItem} data-testid="research-item">
        <ResearchItemHeader
          researchType={ResearchTypes.WHITE_PAPER}
          id={id}
          narrow={narrow}
          postedAt={props.postedAt}
          submittedAt={props.submittedAt}
          title={props.title}
          updatedAt={props.updatedAt}
        />
        <ResearchItemBody
          researchType={ResearchTypes.WHITE_PAPER}
          id={id}
          attachmentExtensions={props.attachmentExtensions}
          elevatorPitch={props.elevatorPitch}
          narrow={narrow}
          subcategories={[
            { label: 'Sectors', categoryNames: props.sectorNames },
            { label: 'Geographies', categoryNames: props.geographyNames },
            { label: 'Esg', categoryNames: props.esgNames },
            { label: 'Global Macro', categoryNames: props.globalMacroNames }
          ]}
          tagList={tagList}
        />
        <ResearchItemFooter
          researchType={ResearchTypes.WHITE_PAPER}
          id={id}
          commentsCount={props.commentsCount}
          narrow={narrow}
          user={props.user}
          showUserDetails={showUserDetails}
        />
      </div>
    );

  return (
    <div
      className={classnames(styles.researchItem, { [styles.narrow]: narrow })}
      data-testid="research-item"
    >
      <ResearchItemHeader
        researchType={ResearchTypes.IDEA}
        id={id}
        canCurrentUserViewIdeaFilterDetails={props.canCurrentUserViewIdeaFilterDetails}
        closedAt={props.closedAt}
        awardWinningContestIdeas={props.awardWinningContestIdeas}
        expectedReturn={props.expectedReturn}
        narrow={narrow}
        positions={props.positions}
        postedAt={props.postedAt}
        submittedAt={props.submittedAt}
        totalReturn={props.totalReturn}
        updatedAt={props.updatedAt}
        weeklyTopIdea={props.weeklyTopIdea}
      />
      <ResearchItemBody
        researchType={ResearchTypes.IDEA}
        id={id}
        attachmentExtensions={props.attachmentExtensions}
        elevatorPitch={props.elevatorPitch}
        narrow={narrow}
        tagList={tagList}
      />
      <ResearchItemFooter
        researchType={ResearchTypes.IDEA}
        id={id}
        anonymous={props.anonymous}
        authorName={props.authorName}
        commentsCount={props.commentsCount}
        expectedPerformanceRating={props.expectedPerformanceRating}
        isNonBuyside={props.isNonBuyside}
        isPublic={Boolean(props.isPublic)}
        narrow={narrow}
        user={props.user}
        showUserDetails={showUserDetails}
      />
    </div>
  );
};

export default ResearchItem;
